import { Component, inject } from '@angular/core';

import { AzureStorageService } from '~/services/azure-storage.service';
import { DocumentFileService } from '~/services/document-file.service';
import { DocumentFinderApiService } from '~/services/document-finder-api.service';

import {
    DOC_TYPES, FoundDocument, SIGNALS, confidenceLabel, landscapeLabel, sourceName
} from '~/models/document-finder.models';
import { DocumentFinderService } from '~/services/document-finder.service';
import { downloadBlob } from '~/utils/download';

@Component({
    selector: 'df-doc-drawer',
    templateUrl: './df-doc-drawer.component.html',
    styleUrls: ['./df-doc-drawer.component.scss'],
    standalone: false
})
export class DfDocDrawerComponent {
    public readonly signals = SIGNALS;
    public readonly docTypes = DOC_TYPES as Record<string, (typeof DOC_TYPES)[keyof typeof DOC_TYPES]>;
    public readonly sourceName = sourceName;
    public readonly landscapeLabel = landscapeLabel;

    public openError = '';
    public downloading = false;
    /** Text preview from GET documents/{id}/preview. */
    public preview: string = '';
    public previewLoading: boolean = false;
    private previewFor: string | null = null;
    private files = inject(DocumentFileService);
    private azure = inject(AzureStorageService);
    private api = inject(DocumentFinderApiService);

    constructor(public df: DocumentFinderService) {}

    public get doc(): FoundDocument | undefined {
        const d = this.df.documents.find(x => x.id === this.df.drawerDocId);
        if (d && d.id !== this.previewFor) { this.loadPreview(d); }
        return d;
    }

    private loadPreview(d: FoundDocument): void {
        this.previewFor = d.id;
        this.preview = '';
        this.openError = '';
        this.previewLoading = false;
        if (d.signavioFlowId) { return; }
        if (this.azure.getUrl(d.id)) { return; }
        this.previewLoading = true;
        this.api.getPreview(d.id)
            .then(text => { if (this.previewFor === d.id) { this.preview = text ?? ''; } })
            .catch(err => console.error('Preview failed', err))
            .finally(() => { if (this.previewFor === d.id) { this.previewLoading = false; } });
    }

    public confLabel(conf: number): string {
        return confidenceLabel(conf).toLowerCase();
    }

    public close(): void {
        this.df.closeAll();
    }

    public include(id: string): void {
        this.df.include(id);
        this.df.closeAll();
    }

    public exclude(id: string): void {
        this.df.openExclude([id]);
    }

    /** Opens the document from Azure Storage (blob) in a new tab; PDFs and images render inline, others download. */
    public async openDocument(id: string): Promise<void> {
        const doc = this.df.documents.find(x => x.id === id);
        if (!doc) { return; }
        if (doc.signavioFlowId) { await this.downloadFlow(doc); return; }
        // Word / Excel / PowerPoint cannot render in Chrome, so open them in the Office web viewer
        const sas = this.azure.getUrl(id);
        if (sas && /^(docx?|xlsx?|pptx?)$/.test(doc.ext)) {
            window.open('https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(sas), '_blank');
            return;
        }
        const win = window.open('', '_blank');
        if (!sas) {
            // API documents: open the source link (GET documents/{id}/link), falling back to the content below
            try {
                const link = await this.api.getLink(id);
                if (link) {
                    if (win) { win.location.href = link; } else { window.open(link, '_blank'); }
                    return;
                }
            } catch (e) {
                console.warn('No link for document, downloading content instead', e);
            }
        }
        try {
            const file = await this.files.getFile(doc);
            const type = this.mime(doc.ext) || file.blob.type;
            const url = URL.createObjectURL(file.blob.type === type ? file.blob : new Blob([file.blob], { type }));
            if (win && /pdf|image|text/.test(type)) {
                win.location.href = url;
            } else {
                win?.close();
                const a = document.createElement('a');
                a.href = url;
                a.download = file.fileName;
                a.click();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e: any) {
            win?.close();
            this.openError = e?.message || 'Could not open the document';
        }
    }

    public async downloadFlow(doc: FoundDocument): Promise<void> {
        if (this.downloading || !doc.signavioFlowId) { return; }
        this.downloading = true;
        this.openError = '';
        try {
            downloadBlob(await this.api.downloadSignavio(doc.signavioFlowId), 'model_1_.svg');
        } catch {
            this.openError = 'Could not download the Signavio flow. Please try again.';
        } finally {
            this.downloading = false;
        }
    }

    private mime(ext: string): string {
        return ({ pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', txt: 'text/plain' } as Record<string, string>)[ext] ?? '';
    }
}
